import { projects } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import { EVENTS } from "@folio-2026/shared"
import { gsap } from "gsap/all"
import {
  Fn,
  mix,
  positionGeometry,
  texture,
  uniform,
  uv,
  vec2,
  vec3,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import Video from "./Video"

export default class Cinema {
  experience: Experience
  scene: THREE.Scene
  mesh!: THREE.Mesh
  material!: THREE.MeshBasicNodeMaterial
  debugFolder?: FolderApi
  videos: Video[] = []

  curvatureUniform = uniform(0.109)
  videoBrightness = uniform(0)
  textureSize = uniform(vec2(1))
  videoTextureNode: THREE.TextureNode

  constructor({
    scene,
    debugFolder,
  }: {
    scene: THREE.Scene
    debugFolder?: FolderApi
  }) {
    this.experience = Experience.getInstance()
    this.scene = scene
    this.debugFolder = debugFolder

    this.videos = projects.map(
      (project) => new Video({ videoID: project.videoID }),
    )

    // Initialize video texture node with first video (can be updated via .value)
    const initialTexture = this.videos[0]?.texture
    if (!initialTexture) {
      throw new Error("No video texture available")
    }
    this.videoTextureNode = texture(initialTexture)

    this.createMesh()
    this.setTextureSize()

    this.setEventListeners()

    this.setDebug()
  }

  setEventListeners() {
    this.experience.on(EVENTS.Works.BeforeEnter, () => {
      this.videoBrightness.value = 0
    })

    this.experience.on(EVENTS.Works.Entered, () => {
      gsap.to(this.videoBrightness, {
        value: 1,
        duration: 0.5,
        ease: "power2.out",
      })
    })

    this.experience.on(EVENTS.Works.SlideEnter, (e: unknown) => {
      const { index } = e as { index: number }
      this.updateVideoTexture(index)
    })
  }

  updateVideoTexture(index: number) {
    const video = this.videos[index]

    if (!video || !video.texture) {
      console.warn(`Video not found for project at index ${index}`)
      return
    }

    this.setTextureSize(video).then(() => {
      this.videoTextureNode.value = video.texture
    })
  }

  createMesh() {
    const geometry = new THREE.PlaneGeometry(8, 3, 32, 18)
    this.material = new THREE.MeshBasicNodeMaterial()

    // Apply curvature using positionNode
    this.material.positionNode = Fn(() => {
      const x = positionGeometry.x
      const y = positionGeometry.y
      const z = positionGeometry.z

      // Apply curvature along x-axis (horizontal curve)
      // Curvature is applied as a quadratic function: z += curvature * x^2
      const xCurvature = this.curvatureUniform.mul(x.mul(x))
      const curvedZ = z.add(xCurvature)

      return vec3(x, y, curvedZ)
    })()

    // Set colorNode once using the texture node - we'll update the node's value when switching videos
    this.material.colorNode = Fn(() => {
      // const offset = vec2(0)
      // const vUv = offset.add(0.5).add(uv().sub(0.5).div(this.textureSize))
      const videoTexture = texture(this.videoTextureNode, uv())
      return mix(vec3(0.003), videoTexture.rgb, this.videoBrightness)
    })()

    this.mesh = new THREE.Mesh(geometry, this.material)
    this.mesh.position.set(7.4, 3.5, -3.91)
    this.mesh.rotation.set(0, -1.093, 0)
    this.mesh.scale.set(2, 2, 2)

    this.scene.add(this.mesh)
  }

  async setTextureSize(video?: Video) {
    const targetVideo = video || this.videos[0]
    if (!targetVideo) {
      return
    }

    // Wait for metadata if needed
    await targetVideo.waitForMetadata()

    const { ratio } = targetVideo.getSize()

    if (!ratio || ratio === 0) {
      return
    }

    const meshSizesAspect = this.mesh.scale.x / this.mesh.scale.y

    let scaleWidth, scaleHeight

    if (ratio > meshSizesAspect) {
      scaleWidth = ratio / meshSizesAspect
      scaleHeight = 1
    } else if (ratio < meshSizesAspect) {
      scaleWidth = 1
      scaleHeight = meshSizesAspect / ratio
    } else {
      scaleWidth = 1
      scaleHeight = 1
    }

    this.textureSize.value.x = scaleWidth
    this.textureSize.value.y = scaleHeight
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "🎥 Cinema",
      expanded: false,
    })

    // Position controllers
    folder.addBinding(this.mesh.position, "x", {
      label: "Position X",
      min: -20,
      max: 20,
      step: 0.01,
    })

    folder.addBinding(this.mesh.position, "y", {
      label: "Position Y",
      min: -20,
      max: 20,
      step: 0.01,
    })

    folder.addBinding(this.mesh.position, "z", {
      label: "Position Z",
      min: -20,
      max: 20,
      step: 0.01,
    })

    // Rotation controllers
    folder.addBinding(this.mesh.rotation, "x", {
      label: "Rotation X",
      min: -Math.PI,
      max: Math.PI,
      step: 0.001,
    })

    folder.addBinding(this.mesh.rotation, "y", {
      label: "Rotation Y",
      min: -Math.PI,
      max: Math.PI,
      step: 0.001,
    })

    folder.addBinding(this.mesh.rotation, "z", {
      label: "Rotation Z",
      min: -Math.PI,
      max: Math.PI,
      step: 0.001,
    })

    // Curvature controller
    folder.addBinding(this.curvatureUniform, "value", {
      label: "Curvature",
      min: -1,
      max: 1,
      step: 0.001,
    })
  }
}
